import {ErrorHandler, inject, Injectable} from '@angular/core';

import {MonitoringService} from '@selpia/user/tracking';
import {Log, LogLevel} from '../models';
import {errorHandlerOptionsToken} from './error-handler.token';


@Injectable({
  providedIn: 'root',
})
export class GlobalErrorHandler implements ErrorHandler {
  readonly #_monitoring = inject(MonitoringService);

  readonly #options = inject(errorHandlerOptionsToken);

  readonly #_consoleError: (...args: unknown[]) => void;
  readonly #_consoleWarning: (...args: unknown[]) => void;
  readonly #_consoleInfo: (...args: unknown[]) => void;
  readonly #_consoleDebug: (...args: unknown[]) => void;
  readonly #_consoleLog: (...args: unknown[]) => void;

  constructor() {
    this.#_consoleError = console.error.bind(console);
    this.#_consoleWarning = console.warn.bind(console);
    this.#_consoleInfo = console.info.bind(console);
    this.#_consoleDebug = console.debug.bind(console);
    this.#_consoleLog = console.log.bind(console);

    this.#hijackConsoleMethods();

    this.#_overwriteWindowOnError();

    // @ts-expect-error: types do not match
    window['errorHandler'] = {log: this.log.bind(this)};
  }

  handleError(error: any): void {
    this.log({
      clientTime: new Date(),
      level: 'error',
      message: 'Uncaught error inside Angular: ',
      error: (error instanceof Error) ? error : new Error(error),
    });
  }

  log(entry: Log): void {
    if (S2WX_FE_DEBUG) {
      this.#_consoleLog('Handler caught log of level:', entry.level);
    }

    if (this.#options.appInsightsLevels.includes(entry.level)) {
      this.#logToInsights(entry);
    }

    if (this.#options.consoleLevels.includes(entry.level)) {
      this.#logToConsole(entry);
    }
  }

  #logToInsights(entry: Log): void {
    const {level, message, error} = entry;

    const severityLevel = level === 'error' ? 3 : level === 'warning' ? 2 : level === 'info' ? 1 : 0;

    const formattedError = new Error(message, {cause: error});

    this.#_monitoring.logException(formattedError, severityLevel);
  }

  #logToConsole(entry: Log): void {
    const {level, message, error} = entry;

    const args = [message, error].filter(arg => arg !== undefined);

    switch (level) {
      case "error":
        this.#_consoleError(...args);
        return;
      case "warning":
        this.#_consoleWarning(...args);
        return;
      case "debug":
        this.#_consoleDebug(...args);
        return;
      case "info":
        this.#_consoleInfo(...args);
    }
  }

  #_overwriteWindowOnError(): void {
    const log = this.log;
    window.addEventListener('error', function (event: ErrorEvent) {
      log({
        clientTime: new Date(),
        level: 'error',
        message: `window.onerror: ${event.message}, at ${event.filename}:${event.lineno}`,
        error: event.error,
      });

      event.preventDefault();
      return true;
    });
  }

  #hijackConsoleMethods(): void {
    const loggerFactory = (level: LogLevel) => (...args: unknown[]) => {
      const error = args.find(arg => arg instanceof Error);
      const message = args.filter(arg => typeof arg === 'string')
        .join(', ');

      this.log({
        clientTime: new Date(),
        level,
        message,
        error,
      });
    };

    console.error = loggerFactory('error');
    console.warn = loggerFactory('warning');
    console.debug = loggerFactory('debug');
    console.log = loggerFactory('debug');
    console.info = loggerFactory('info');
  }
}
